export interface ReactionDefinition {
  reactionTypeId: number;
  name: string;
  emoji: string;
  iconKey: string;
}

export const REACTION_DEFINITIONS: ReactionDefinition[] = [
  { reactionTypeId: 1, name: 'Like', emoji: '❤️', iconKey: 'I love this' },
  { reactionTypeId: 2, name: 'Love', emoji: '👍', iconKey: 'I like this' },
  { reactionTypeId: 3, name: 'Celebrate', emoji: '🎉', iconKey: 'Congratulations!' },
  { reactionTypeId: 4, name: 'Insightful', emoji: '💡', iconKey: 'Insightful' },
  { reactionTypeId: 5, name: 'Support', emoji: '💙', iconKey: "I'm here for you" },
  { reactionTypeId: 6, name: 'Encouragement', emoji: '🫶', iconKey: 'Sending love and support' },
  { reactionTypeId: 7, name: 'Care', emoji: '🤗', iconKey: 'Sending a warm hug' },
  { reactionTypeId: 8, name: 'Cheer', emoji: '🙌', iconKey: 'You can do it!' },
  { reactionTypeId: 9, name: 'Clap', emoji: '👏', iconKey: 'Great job!' },
  { reactionTypeId: 10, name: 'Strong', emoji: '💪', iconKey: 'Keep going!' },
  { reactionTypeId: 11, name: 'Fire', emoji: '🔥', iconKey: 'Amazing progress!' },
  { reactionTypeId: 12, name: 'Happy', emoji: '😊', iconKey: 'This makes me happy' },
  { reactionTypeId: 13, name: 'Thankful', emoji: '🙏', iconKey: 'Thank you!' },
  { reactionTypeId: 14, name: 'Achievement', emoji: '🏆', iconKey: 'Well done!' },
  { reactionTypeId: 15, name: 'Agree', emoji: '💯', iconKey: 'Absolutely!' },
  { reactionTypeId: 16, name: 'Wow', emoji: '😲', iconKey: "That's impressive!" },
  { reactionTypeId: 17, name: 'Together', emoji: '🤝', iconKey: "We're in this together" },
  { reactionTypeId: 18, name: 'Amazing', emoji: '✨', iconKey: "You're doing amazing!" },
  { reactionTypeId: 19, name: 'Excited', emoji: '🥳', iconKey: 'So happy for you!' },
  { reactionTypeId: 20, name: 'Proud', emoji: '💖', iconKey: 'So proud of you!' },
  { reactionTypeId: 21, name: 'Best Wishes', emoji: '💐', iconKey: 'Wishing you the best!' },
  { reactionTypeId: 22, name: 'Winner', emoji: '🥇', iconKey: 'You did it!' },
  { reactionTypeId: 23, name: 'Motivated', emoji: '🚀', iconKey: 'Keep moving forward!' },
  { reactionTypeId: 24, name: 'Admire', emoji: '😍', iconKey: 'This is wonderful!' },
  { reactionTypeId: 25, name: 'Sending Love', emoji: '💕', iconKey: 'Lots of love!' },
  { reactionTypeId: 26, name: 'Healthy Choice', emoji: '🥗', iconKey: 'Great healthy choice!' },
];

/** The 6 quick reactions displayed in the Instagram-style floating reaction bar */
export const QUICK_REACTION_TYPE_IDS: readonly number[] = [1, 2, 16, 3, 10, 11];

export const QUICK_REACTIONS: readonly ReactionDefinition[] = QUICK_REACTION_TYPE_IDS
  .map(id => REACTION_DEFINITIONS.find(r => r.reactionTypeId === id)!)
  .filter(Boolean);

const REACTION_BY_ID = new Map<number, ReactionDefinition>(
  REACTION_DEFINITIONS.map(r => [r.reactionTypeId, r]),
);

const REACTION_BY_NAME = new Map<string, ReactionDefinition>(
  REACTION_DEFINITIONS.map(r => [r.name.trim().toLowerCase(), r]),
);

export const getReactionById = (reactionTypeId?: number | null): ReactionDefinition | undefined => {
  if (reactionTypeId == null || reactionTypeId <= 0) return undefined;
  return REACTION_BY_ID.get(reactionTypeId);
};

export const getReactionByName = (name?: string | null): ReactionDefinition | undefined => {
  if (!name) return undefined;
  const normalized = name.trim().toLowerCase();
  if (normalized === 'heart') return REACTION_BY_ID.get(1);
  return REACTION_BY_NAME.get(normalized);
};

export const getReactionDefinition = (
  reactionTypeId?: number | null,
  reactionName?: string | null,
): ReactionDefinition => {
  const byId = getReactionById(reactionTypeId);
  if (byId) return byId;
  const byName = getReactionByName(reactionName);
  if (byName) return byName;
  return getReactionById(1) || REACTION_DEFINITIONS[0]; // Default Like
};

export const getReactionEmoji = (
  reactionTypeId?: number | null,
  reactionName?: string | null,
): string => {
  return getReactionDefinition(reactionTypeId, reactionName).emoji;
};
